// src/components/ProductList.tsx

import React from "react";
import ProductCard from "./ProductCard";
import { CatalogObject } from "square";
import ProductCarousel from "./ProductCarousel";

interface ProductListProps {
  domain?: CatalogObject;
  category?: CatalogObject;
  items: CatalogObject[];
  onClickProduct?: (product: CatalogObject) => void;
  isSmallVersion?: boolean;
  forceReload?: boolean;
  isCarousel?: boolean;
}

const ProductList: React.FC<ProductListProps> = ({
  domain,
  category,
  items,
  onClickProduct,
  isSmallVersion = false,
  forceReload = false,
  isCarousel = false,
}) => {
  // ORDERING IS THE CALLER'S JOB — this renders `items` exactly as given.
  //
  // This used to re-sort by the numeric "Order" custom attribute, in place, on
  // every render. Because it ran LAST, it overrode whatever the caller had
  // already decided: the shopper's sort dropdown on a category page did nothing,
  // search results lost their relevance ranking, and the wishlist and
  // return-page lists were reshuffled. Merchandising order now comes from
  // Square's "Rearrange Items" arrangement via `byMerchandisingOrder`, applied
  // by the callers that actually want it (2026-08-23 owner decision).
  //
  // The "Order" custom attribute is still SET in Square and is read by
  // `byMerchandisingOrder` as a FALLBACK when the ordinal can't decide — but it
  // is never read HERE, because sorting in a leaf component is what caused the
  // overrides above. See docs/catalog-change-checklist.md § "Product display
  // order".
  const productCards = items?.map((o: CatalogObject) => {
    return (
      <ProductCard
        // Key on the stable item id, not the array index: instant search
        // reorders this list on every keystroke, and index keys let React
        // reuse a card in place — flashing the previous product's image in
        // the tile for a frame.
        key={o.id}
        domain={domain}
        category={category}
        product={o}
        onClick={onClickProduct}
        isSmallVersion={isSmallVersion}
        forceReload={forceReload}
      />
    );
  });

  return !!isCarousel ? (
    <ProductCarousel products={productCards} />
  ) : (
    // The compact list lives in a ~26rem drawer, where the page grid's 3–4
    // columns would squeeze each tile to nothing.
    <div
      className={
        isSmallVersion ? "grid grid-cols-2 gap-3" : "page-productlist-boxes"
      }
    >
      {productCards}
    </div>
  );
};

export default ProductList;
